/**
 * ReservationBanner — Countdown timer showing time remaining on tee time hold
 * Uses localStorage for persistence (key: 'currents_reservation')
 */
import { useState, useEffect, useCallback } from 'preact/hooks'
import type { JSX } from 'preact'

const STORAGE_KEY = 'currents_reservation'
const HOLD_DURATION_MS = 10 * 60 * 1000 // 10 minutes

export interface ReservationData {
  teeTimeId: string
  teeTime: string
  courseName: string
  expiresAt: number // timestamp ms
}

export function getSavedReservation(): ReservationData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data: ReservationData = JSON.parse(raw)
    if (Date.now() > data.expiresAt) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return data
  } catch {
    return null
  }
}

export function saveReservation(teeTimeId: string, teeTime: string, courseName: string): ReservationData {
  const data: ReservationData = {
    teeTimeId,
    teeTime,
    courseName,
    expiresAt: Date.now() + HOLD_DURATION_MS,
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  return data
}

export function clearReservation(): void {
  localStorage.removeItem(STORAGE_KEY)
}

interface ReservationBannerProps {
  reservation: ReservationData | null
  onExpired: () => void
  onClear: () => void
}

export function ReservationBanner({ reservation, onExpired, onClear }: ReservationBannerProps): JSX.Element | null {
  const [timeLeft, setTimeLeft] = useState<number>(0)

  useEffect(() => {
    if (!reservation) return

    const update = () => {
      const remaining = Math.max(0, reservation.expiresAt - Date.now())
      setTimeLeft(remaining)
      if (remaining <= 0) {
        clearReservation()
        onExpired()
      }
    }

    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [reservation, onExpired])

  if (!reservation || timeLeft <= 0) return null

  const minutes = Math.floor(timeLeft / 60000)
  const seconds = Math.floor((timeLeft % 60000) / 1000)
  const isUrgent = timeLeft < 2 * 60 * 1000 // less than 2 minutes

  return (
    <div className={`bw2-reservation${isUrgent ? ' bw2-reservation--urgent' : ''}`} role="timer" aria-live="polite">
      <div className="bw2-reservation__info">
        <span className="bw2-reservation__icon">⏱</span>
        <span className="bw2-reservation__text">
          Tee time held for{' '}
          <strong className="bw2-reservation__time">
            {minutes}:{seconds.toString().padStart(2, '0')}
          </strong>
        </span>
      </div>
      <button
        className="bw2-reservation__clear"
        onClick={onClear}
        type="button"
        aria-label="Release tee time hold"
      >
        Release
      </button>
    </div>
  )
}
